import { useMemo } from "react";
import { attachmentsListParams } from "@/features/attachments/lib/list-query-params";
import type {
	AttachmentsAdapter,
	AttachmentsDeleteVars,
	AttachmentsEditVars,
	AttachmentsUploadVars,
} from "@/features/attachments/types";
import {
	useDeleteTicketFile,
	useEditTicketFile,
	useUploadTicketFile,
} from "@/services/ticket/ticket.mutations";
import { ticketQueryOptions } from "@/services/ticket/ticket.options";

/**
 * Pure list query options for ticket attachments (route loaders).
 */
export function createTicketAttachmentsListQueryOptions(args: {
	ticketId: string;
	organizationId: string;
	start: number;
	count: number;
}) {
	return ticketQueryOptions.files.list(
		args.ticketId,
		args.start,
		args.count,
		attachmentsListParams(args.organizationId),
		{ organizationId: args.organizationId },
	);
}

/**
 * Ticket-scoped attachments adapter (hooks + query option factories).
 */
export function useTicketAttachmentsAdapter(
	ticketId: string,
): AttachmentsAdapter {
	const uploadTicket = useUploadTicketFile();
	const deleteTicket = useDeleteTicketFile();
	const editTicket = useEditTicketFile();

	return useMemo(
		(): AttachmentsAdapter => ({
			kind: "ticket",
			entityId: ticketId,
			listQueryOptions: ({ organizationId, start, count }) =>
				createTicketAttachmentsListQueryOptions({
					ticketId,
					organizationId,
					start,
					count,
				}),
			contentQueryOptions: ({ organizationId, fileInfoId }) =>
				ticketQueryOptions.files.content(ticketId, fileInfoId, {
					organizationId,
				}),
			thumbnailQueryOptions: ({ organizationId, fileInfoId }) =>
				ticketQueryOptions.files.thumbnail(ticketId, fileInfoId, {
					organizationId,
				}),
			uploadMutation: {
				mutateAsync: (vars: AttachmentsUploadVars) =>
					uploadTicket.mutateAsync({
						ticketId: vars.entityId,
						file: vars.file,
						organizationId: vars.organizationId,
						description: vars.description,
						entityAttachmentRequirementTagId:
							vars.entityAttachmentRequirementTagId,
						onUploadProgress: vars.onUploadProgress,
					}),
				reset: () => uploadTicket.reset(),
				isPending: uploadTicket.isPending,
				error: (uploadTicket.error as Error | null) ?? null,
			},
			deleteMutation: {
				mutateAsync: (vars: AttachmentsDeleteVars) =>
					deleteTicket.mutateAsync({
						ticketId: vars.entityId,
						fileId: vars.fileId,
						organizationId: vars.organizationId,
					}),
				reset: () => deleteTicket.reset(),
				isPending: deleteTicket.isPending,
				error: (deleteTicket.error as Error | null) ?? null,
			},
			editMutation: {
				mutateAsync: (vars: AttachmentsEditVars) =>
					editTicket.mutateAsync({
						ticketId: vars.entityId,
						fileId: vars.fileId,
						name: vars.name,
						description: vars.description,
						organizationId: vars.organizationId,
					}),
				reset: () => editTicket.reset(),
				isPending: editTicket.isPending,
				error: (editTicket.error as Error | null) ?? null,
			},
		}),
		[deleteTicket, editTicket, ticketId, uploadTicket],
	);
}
